import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Archive, ArchiveRestore, ArrowLeft, Award, CalendarRange, ChevronLeft, ChevronRight, Copy, Pencil, Timer, Trash2, Undo2 } from 'lucide-react';
import {
  addDays,
  addMonths,
  alignBack,
  endOfMonth,
  isoWeekday,
  startOfMonth,
  statusOnDate,
  type LocalDate,
} from '@habitos/engine';
import { useI18n } from '../i18n';
import { notesLinkedTo } from '../domain/notes';
import { useApp } from '../state';
import { habitStats, type HabitStats } from '../domain/selectors';
import {
  archiveHabit,
  dayTotal,
  deleteEntry,
  deleteHabit,
  duplicateHabit,
  logAmount,
  PlanLimitError,
  undoMove,
  setOverride,
  setTaskDone,
  toggleBinary,
} from '../domain/actions';
import { currentVersion, currentWeekStartOf, type HabitRec } from '../domain/model';
import { HabitIcon } from '../ui/HabitIcon';
import { Field } from '../ui/Field';
import { useToast } from '../ui/Toast';
import { errorMessage, progressText, streakText, unitLabel } from '../ui/format';
import { MovePanel } from './HabitActions';
import { Sheet } from '../ui/Sheet';
import { RemindersPanel } from './RemindersPanel';

export function HabitDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { data, ctx, idx } = useApp();
  const habit = data.habits.find((h) => h.id === id);
  const stats = useMemo(() => (habit && habit.kind !== 'task' ? habitStats(habit, data, idx, ctx.today) : null), [habit, data, idx, ctx.today]);

  if (!habit) {
    return (
      <div className="stack">
        <p>{t('habits.empty')}</p>
        <Link to="/habits" className="btn">
          {t('common.back')}
        </Link>
      </div>
    );
  }
  return <DetailBody habit={habit} stats={stats} />;
}

function DetailBody({ habit, stats }: { habit: HabitRec; stats: HabitStats | null }) {
  const { t, fmtNumber, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const navigate = useNavigate();
  const toast = useToast();
  const [month, setMonth] = useState<LocalDate>(startOfMonth(ctx.today));
  const [selected, setSelected] = useState<LocalDate | null>(null);
  const isTask = habit.kind === 'task';
  const hideStreak = habit.hideStreak || ctx.settings.hideStreaks;

  const run = async (fn: () => Promise<unknown>, msg?: string) => {
    try {
      await fn();
      if (msg) toast.show(msg);
    } catch (err) {
      if (err instanceof PlanLimitError) toast.show(t('editor.planLimit', { limit: err.limit }));
      else toast.show(errorMessage(t, err));
    }
  };

  const pct = (r: number | null) => (r === null ? t('common.dash') : `${Math.round(r * 100)} %`);

  return (
    <div className="stack-l" data-color={habit.color}>
      <header className="page-header" style={{ marginBottom: 0, alignItems: 'center' }}>
        <div className="row" style={{ minWidth: 0 }}>
          <button type="button" className="icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            <ArrowLeft size={22} aria-hidden="true" />
          </button>
          <div className="habit__icon">
            <HabitIcon name={habit.icon} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ overflowWrap: 'anywhere' }}>{habit.name}</h1>
            <p className="page-header__sub" style={{ textTransform: 'none' }}>
              {t(`habit.kinds.${habit.kind}`)}
              {habit.archivedAt ? ` · ${t('habits.archived')}` : ''}
            </p>
          </div>
        </div>
        <Link to={`/habits/${habit.id}/edit`} className="icon-btn" aria-label={t('habit.actions.edit')}>
          <Pencil size={20} aria-hidden="true" />
        </Link>
      </header>

      {habit.description && <p className="muted">{habit.description}</p>}

      {isTask ? (
        <section className="card stack">
          <p>
            {t(`habit.status.${habit.completedAt ? 'completed' : habit.dueDate && habit.dueDate < ctx.today ? 'overdue' : 'pending'}`)}
            {habit.dueDate ? ` · ${t('editor.dueDate')}: ${fmtDate(habit.dueDate, { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
          </p>
          <button type="button" className="btn btn--primary" onClick={() => run(() => setTaskDone(habit, !habit.completedAt))}>
            {habit.completedAt ? t('habit.actions.uncomplete') : t('habit.actions.complete')}
          </button>
        </section>
      ) : (
        stats && (
          <>
            <section className="stat-grid" aria-label={t('stats.title')}>
              {!hideStreak && (
                <>
                  <div className="stat">
                    <div className="stat__value">{streakText(t, stats.streak.current, stats.streak.unit)}</div>
                    <div className="stat__label">{t('detail.currentStreak')}</div>
                  </div>
                  <div className="stat">
                    <div className="stat__value">{streakText(t, stats.streak.best, stats.streak.unit)}</div>
                    <div className="stat__label">{t('detail.bestStreak')}</div>
                  </div>
                </>
              )}
              <div className="stat" title={t('detail.rateHelp')}>
                <div className="stat__value">{pct(stats.rate.rate)}</div>
                <div className="stat__label">{t('detail.successRate')}</div>
              </div>
              <div className="stat">
                <div className="stat__value">{pct(stats.rate30.rate)}</div>
                <div className="stat__label">{t('detail.last30')}</div>
              </div>
              <div className="stat" title={t('detail.gapsHelp')}>
                <div className="stat__value">{fmtNumber(stats.gaps30.count)}</div>
                <div className="stat__label">{t('detail.gaps30')}</div>
                {stats.gaps30.longest > 1 && (
                  <div className="muted xsmall">{t('detail.gapsLongest', { length: streakText(t, stats.gaps30.longest, stats.gaps30.unit) })}</div>
                )}
              </div>
              <div className="stat">
                <div className="stat__value">{fmtNumber(stats.completions)}</div>
                <div className="stat__label">{t('detail.completions')}</div>
              </div>
              {stats.relapseDays !== null && (
                <div className="stat">
                  <div className="stat__value">{fmtNumber(stats.relapseDays)}</div>
                  <div className="stat__label">{t('detail.relapse')}</div>
                </div>
              )}
              {currentVersion(habit).goalType === 'numeric' && stats.avgProgress !== null && (
                <div className="stat">
                  <div className="stat__value">{pct(stats.avgProgress)}</div>
                  <div className="stat__label">{t('detail.avgProgress')}</div>
                </div>
              )}
            </section>
            <p className="muted xsmall">{t('detail.rateHelp')}</p>

            <Calendar habit={habit} stats={stats} month={month} setMonth={setMonth} selected={selected} setSelected={setSelected} />

            {selected ? (
              <DayPanel habit={habit} stats={stats} date={selected} />
            ) : (
              <p className="muted small">
                {t('detail.selectDay')} {t('detail.retroNote')}
              </p>
            )}

            {notesLinkedTo(data.notes, habit.id).length > 0 && (
              <section className="stack" aria-labelledby="linked-notes-title">
                <h2 id="linked-notes-title">{t('brain.linkedTitle')}</h2>
                <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {notesLinkedTo(data.notes, habit.id).map((n) => (
                    <li key={n.id} className="card small" style={{ overflowWrap: 'anywhere' }}>
                      <strong>{n.title}</strong>
                      {n.body && <div className="muted">{n.body.slice(0, 140)}</div>}
                    </li>
                  ))}
                </ul>
                <Link to="/brain" className="small">
                  {t('brain.title')}
                </Link>
              </section>
            )}

            <section className="stack" aria-label={t('detail.achievements')}>
              <h2>{t('detail.achievements')}</h2>
              {stats.achievements.length === 0 ? (
                <p className="muted small">{t('detail.noAchievements')}</p>
              ) : (
                <div className="achievements">
                  {stats.achievements.map((a) => {
                    const [kind, n] = a.id.split('_');
                    return (
                      <span key={a.id} className="badge badge--accent" title={fmtDate(a.achievedOn)}>
                        <Award size={14} aria-hidden="true" />
                        {kind === 'streak'
                          ? t('achievements.streak', { count: streakText(t, Number(n), stats.streak.unit) })
                          : t('achievements.total', { count: Number(n) })}
                      </span>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )
      )}

      <RemindersPanel habit={habit} />

      <section className="list-card">
        {habit.kind !== 'quit' && !habit.archivedAt && (
          <Link to={`/focus?habit=${habit.id}`} className="list-item">
            <Timer size={20} aria-hidden="true" />
            <span className="list-item__body">{t('focus.openFor')}</span>
          </Link>
        )}
        <button
          type="button"
          className="list-item"
          onClick={() => run(async () => {
            await duplicateHabit(habit, data.habits, t('habit.duplicateSuffix'));
            navigate('/');
          })}
        >
          <Copy size={20} aria-hidden="true" />
          <span className="list-item__body">{t('habit.actions.duplicate')}</span>
        </button>
        <button type="button" className="list-item" onClick={() => run(() => archiveHabit(habit, ctx, !habit.archivedAt), habit.archivedAt ? undefined : t('habit.archivedToast'))}>
          {habit.archivedAt ? <ArchiveRestore size={20} aria-hidden="true" /> : <Archive size={20} aria-hidden="true" />}
          <span className="list-item__body">{habit.archivedAt ? t('habit.actions.unarchive') : t('habit.actions.archive')}</span>
        </button>
        <button
          type="button"
          className="list-item"
          style={{ color: 'var(--danger)' }}
          onClick={() =>
            run(async () => {
              await deleteHabit(habit, true);
              navigate('/habits');
              toast.show(t('habit.deletedToast'), { label: t('common.undo'), run: () => void deleteHabit(habit, false) });
            })
          }
        >
          <Trash2 size={20} aria-hidden="true" />
          <span className="list-item__body">{t('habit.actions.delete')}</span>
        </button>
      </section>
    </div>
  );
}

function Calendar({
  habit,
  stats,
  month,
  setMonth,
  selected,
  setSelected,
}: {
  habit: HabitRec;
  stats: HabitStats;
  month: LocalDate;
  setMonth: (d: LocalDate) => void;
  selected: LocalDate | null;
  setSelected: (d: LocalDate) => void;
}) {
  const { t, m, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const ws = currentWeekStartOf(ctx.settings);
  const first = alignBack(month, ws);
  const last = endOfMonth(month);
  const days: LocalDate[] = [];
  for (let d = first; d <= last || isoWeekday(d) !== ws; d = addDays(d, 1)) days.push(d);
  const headers = Array.from({ length: 7 }, (_, i) => ((ws - 1 + i) % 7) + 1);
  const canNext = addMonths(month, 1) <= ctx.today;
  const canPrev = month > startOfMonth(habit.startDate);
  const title = fmtDate(month, { month: 'long', year: 'numeric' });

  return (
    <section className="card stack" aria-label={t('detail.calendar')}>
      <div className="row">
        <h2 className="spacer" aria-live="polite">
          {title}
        </h2>
        <button type="button" className="icon-btn" disabled={!canPrev} onClick={() => setMonth(addMonths(month, -1))} aria-label={t('detail.prevMonth')}>
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <button type="button" className="icon-btn" disabled={!canNext} onClick={() => setMonth(addMonths(month, 1))} aria-label={t('detail.nextMonth')}>
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </div>
      <div className="cal" role="group" aria-label={title}>
        {headers.map((d) => (
          <div key={d} className="cal__head" aria-hidden="true">
            {m.weekdays.short[d - 1]}
          </div>
        ))}
        {days.map((d) => {
          const inMonth = d.slice(0, 7) === month.slice(0, 7);
          const future = d > ctx.today;
          const { status } = future ? { status: 'not_scheduled' as const } : statusOnDate(
            { id: habit.id, kind: habit.kind, startDate: habit.startDate, endDate: habit.endDate, archivedAt: habit.archivedAt, versions: habit.versions },
            stats.results,
            d,
          );
          const total = dayTotal(data.entries, habit.id, d);
          const label = `${fmtDate(d, { weekday: 'long', day: 'numeric', month: 'long' })}: ${t(`habit.status.${status}`)}${total > 0 ? `, ${total}` : ''}`;
          if (!inMonth) return <span key={d} aria-hidden="true" />;
          return (
            <button
              key={d}
              type="button"
              className="cal__day num"
              data-status={status}
              data-partial={total > 0 && status !== 'completed' ? 'true' : undefined}
              data-today={d === ctx.today ? 'true' : undefined}
              aria-pressed={selected === d}
              aria-label={label}
              disabled={future || d < habit.startDate}
              onClick={() => setSelected(d)}
            >
              {Number(d.slice(8))}
            </button>
          );
        })}
      </div>
      <div className="legend" aria-label={t('detail.legend')}>
        <span><i className="legend__dot" style={{ background: 'var(--hc)' }} />{t('habit.status.completed')}</span>
        <span><i className="legend__dot" style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger)' }} />{t('habit.status.missed')}</span>
        <span><i className="legend__dot" style={{ background: 'var(--skip-soft)', border: '1px solid var(--skip)' }} />{t('habit.status.skipped')}</span>
        <span><i className="legend__dot" style={{ background: 'var(--vacation-soft)', border: '1px solid var(--vacation)' }} />{t('habit.status.vacation')}</span>
      </div>
    </section>
  );
}

function DayPanel({ habit, stats, date }: { habit: HabitRec; stats: HabitStats; date: LocalDate }) {
  const { t, m, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const def = { id: habit.id, kind: habit.kind, startDate: habit.startDate, endDate: habit.endDate, archivedAt: habit.archivedAt, versions: habit.versions };
  const { status, ue } = statusOnDate(def, stats.results, date);
  // Traslados que tocan este día: como origen (se fue a otro) o como destino (vino de otro).
  const movedAway = data.moves.find((mo) => mo.habitId === habit.id && mo.fromDate === date);
  const movedHere = data.moves.find((mo) => mo.habitId === habit.id && mo.toDate === date);
  const canMove = !!ue && ue.kind === 'day' && habit.kind !== 'task' && ['pending', 'missed'].includes(ue.status);
  const entries = data.entries.filter((e) => e.habitId === habit.id && e.logicalDate === date).sort((a, b) => (a.occurredAt < b.occurredAt ? -1 : 1));
  const v = ue?.version ?? currentVersion(habit);
  const binary = habit.kind === 'build' && v.goalType === 'binary';
  const unit = unitLabel(m, v.unit);

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      toast.show(errorMessage(t, err));
    }
  };

  return (
    <section className="card stack" aria-label={t('detail.history')}>
      <div className="row row--wrap">
        <h2 className="spacer">{fmtDate(date, { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
        <span className={`badge badge--${status}`}>{t(`habit.status.${status}`)}</span>
      </div>
      {ue && <p className="muted small num">{progressText(t, m, habit, ue)}</p>}
      {(movedAway ?? movedHere) && (
        <div className="row row--wrap">
          <span className="small spacer">
            {movedAway
              ? t('habit.movedAway', { date: fmtDate(movedAway.toDate, { weekday: 'long', day: 'numeric', month: 'long' }) })
              : t('habit.movedHere', { date: fmtDate(movedHere!.fromDate, { weekday: 'long', day: 'numeric', month: 'long' }) })}
          </span>
          <button type="button" className="btn btn--outline btn--sm" onClick={() => run(() => undoMove((movedAway ?? movedHere)!))}>
            <Undo2 size={16} aria-hidden="true" /> {t('habit.actions.undoMove')}
          </button>
        </div>
      )}
      {entries.length === 0 ? (
        <p className="muted small">{t('detail.noEntries')}</p>
      ) : (
        <div>
          {entries.map((e) => (
            <div key={e.id} className="entry-row">
              <span className="num" style={{ fontWeight: 750 }}>
                {e.value > 0 ? '+' : ''}
                {e.value} {unit}
              </span>
              <span className="muted xsmall spacer">
                {new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', timeZone: e.tz }).format(new Date(e.occurredAt))}
                {e.note ? ` · ${e.note}` : ''}
              </span>
              <button type="button" className="icon-btn" aria-label={t('detail.deleteEntry')} onClick={() => run(() => deleteEntry(e.id))}>
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      {canMove && !movedHere && (
        <button type="button" className="btn btn--outline" onClick={() => setMoveOpen(true)}>
          <CalendarRange size={18} aria-hidden="true" /> {t('habit.actions.move')}
        </button>
      )}
      <Sheet open={moveOpen} onClose={() => setMoveOpen(false)} title={t('habit.actions.move')}>
        <MovePanel habit={habit} from={date} onDone={() => setMoveOpen(false)} />
      </Sheet>
      {status !== 'not_scheduled' && status !== 'out_of_range' && (
        <>
          {binary ? (
            <button type="button" className="btn btn--primary" onClick={() => run(() => toggleBinary(habit, date, ctx, data.entries, data.overrides))}>
              {dayTotal(data.entries, habit.id, date) >= 1 ? t('habit.actions.uncomplete') : t('habit.actions.complete')}
            </button>
          ) : (
            <form
              className="row"
              style={{ alignItems: 'flex-end' }}
              onSubmit={(e) => {
                e.preventDefault();
                const n = Number(amount.replace(',', '.'));
                if (!Number.isFinite(n) || n === 0) return;
                void run(async () => {
                  await logAmount(habit, date, n, ctx, data.entries, data.overrides);
                  setAmount('');
                });
              }}
            >
              <div className="spacer">
                <Field label={`${t('detail.addEntry')}${unit ? ` (${unit})` : ''}`}>
                  {(p) => <input {...p} className="input num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />}
                </Field>
              </div>
              <button type="submit" className="btn btn--primary">
                {t('common.add')}
              </button>
            </form>
          )}
          <div className="row row--wrap">
            {status === 'skipped' ? (
              <button type="button" className="btn btn--sm" onClick={() => run(() => setOverride(habit, date, null, ctx))}>
                {t('habit.actions.unskip')}
              </button>
            ) : (
              <button type="button" className="btn btn--sm" onClick={() => run(() => setOverride(habit, date, 'skipped', ctx))}>
                {t('habit.actions.skip')}
              </button>
            )}
            {status === 'failed' ? (
              <button type="button" className="btn btn--sm" onClick={() => run(() => setOverride(habit, date, null, ctx))}>
                {t('habit.actions.unfail')}
              </button>
            ) : (
              <button type="button" className="btn btn--sm" onClick={() => run(() => setOverride(habit, date, 'failed', ctx))}>
                {t('habit.actions.fail')}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
